import React, { useState } from 'react'
import classNames from 'classnames'
import Typist from 'react-typist'

import Layout from '../components/layout'
import { Section, Column, ProdigySlider, LogoRow, FeaturesRow } from '../components/landing'
import { UserLogos, Testimonials } from '../components/landing'
import Link, { Button } from '../components/link'
import { TerminalWrapper, Terminal, Command } from '../components/terminal'
import { InlineCode, CodeBlock } from '../components/code'
import { YouTube } from '../components/embed'
import Footer from '../components/footer'
import NoScript from '../components/noscript'
import Logo from '../images/logo.svg'
import ExplosionLogo from '../images/logo_explosion.svg'
import Blob from '../images/blob.svg'
import classes from '../styles/landing.module.sass'

import uiExamples from './examples.json'

const cliPlaceholder = (
    <>
        pip install ./prodigy.whl
        <br />
        Successfully installed prodigy
        <br />
        <br />
        prodigy ner.manual reviews_ner en_core_web_sm ./data.jsonl --label PRODUCT,PERSON,ORG
        <br />
        <br />
        ✨ Starting the web server on port 8080...
        <br />
        Open the app in your browser and start annotating!
    </>
)

const TerminalAnimation = () => {
    const cursor = { element: '█', hideWhenDone: true, hideWhenDoneDelay: 0 }
    const [done, setDone] = useState(false)
    return (
        <InlineCode>
            <NoScript placeholder={cliPlaceholder}>
                <Typist cursor={cursor} onTypingDone={() => setDone(true)}>
                    <span className={classes.typingPrompt}>
                        <span className={classes.typingCmd}>pip</span> install <u>./prodigy.whl</u>
                    </span>
                    <br />
                    ███████████████████████████████████ 100%
                    <br />
                    <br />
                    <span className={classes.typingPrompt}>
                        <span className={classes.typingCmd}>prodigy</span> ner.manual reviews_ner
                        en_core_web_sm <u>./data.jsonl</u> --label PRODUCT,PERSON,ORG
                    </span>
                    <Typist.Delay ms={500} />
                </Typist>
                {done && (
                    <>
                        <br />
                        ✨ Starting the web server on port 8080...
                        <br />
                        Open the app in your browser and start annotating!
                    </>
                )}
            </NoScript>
        </InlineCode>
    )
}

export default () => (
    <Layout navClassName={classes.nav}>
        <header className={classNames(classes.header, classes.sectionDark)}>
            <Link to="https://explosion.ai" hidden>
                <ExplosionLogo width={65} height={65} className={classes.logoExplosion} />
            </Link>
            <Logo width={325} height={95} className={classes.logo} />
            <h3 className={classes.title}>
                Radically efficient machine teaching.
                <br className={classes.br} />
                An annotation tool powered
                <br className={classes.br} />
                by active learning.
            </h3>

            <div className={classes.label}>From the makers of spaCy</div>
        </header>

        <Section dark last columns={['610px', '610px']}>
            <Column className={classes.terminalWrapper}>
                <Blob className={classes.blob} />
                <TerminalWrapper className={classes.terminal}>
                    <TerminalAnimation />
                </TerminalWrapper>
            </Column>
            <Column title="Train a new AI model in hours">
                <p>
                    Prodigy is a scriptable annotation tool so efficient that data scientists can do
                    the annotation themselves, enabling a new level of rapid iteration.
                </p>
                <p>
                    Today’s transfer learning technologies mean you can train production-quality
                    models with very few examples. With Prodigy you can take full advantage of
                    modern machine learning by adopting a more agile approach to data collection.
                    You'll move faster, be more independent and ship far more successful projects.
                </p>

                <Button to="/docs" large>
                    How it works
                </Button>
            </Column>
        </Section>

        <Section columns={['600px', '610px']}>
            <Column title="The missing piece in your data science workflow">
                <p>
                    Prodigy brings together state-of-the-art insights from machine learning and user
                    experience. With its continuous active learning system, you're only asked to
                    annotate examples the model does not already know the answer to. The web
                    application is powerful, extensible and follows modern UX principles. The secret
                    is very simple: it's designed to help you focus on one decision at a time and
                    keep you clicking – like Tinder for data.
                </p>
                <p>
                    Everyone knows data scientists should spend more time looking at their data.
                    When good habits are hard to form, the trick is to remove the friction. Prodigy
                    makes the right thing easy, encouraging you to spend more time understanding
                    your problem and interpreting your results.
                </p>

                <Button to="/demo" large target="_blank">
                    Try the demo
                </Button>
            </Column>
            <Column>
                <ProdigySlider data={uiExamples} />
            </Column>
        </Section>

        <UserLogos />

        <Section columns={['610px', '610px']}>
            <Column>
                <YouTube id="59BKHO_xBPA" />
            </Column>
            <Column title="Try out new ideas quickly">
                <p>
                    Annotation is usually the part where projects stall. Instead of having an idea
                    and trying it out, you start scheduling meetings, writing specifications and
                    dealing with quality control. With Prodigy, you can have an idea over breakfast
                    and get your first results by lunch. Once the model is trained, you can export
                    it as a versioned Python package, giving you a smooth path from prototype to
                    production.
                </p>
                <Button to="/docs" large>
                    Read more
                </Button>
            </Column>
        </Section>

        <Testimonials />

        <Section columns={['610px', '610px']}>
            <Column title="Fully scriptable and extensible">
                <p>
                    Prodigy is fully scriptable, and slots neatly into the rest of your Python-based
                    data science workflow. As the makers of spaCy, a popular library for Natural
                    Language Processing, we understand how to make tools programmers love. The
                    simple secret is this: programmers want to be able to program. Good developer
                    tools need to let you in, not lock you out. That's why Prodigy comes with a rich
                    Python API, elegant command-line integration, and a super productive Jupyter
                    extension. Using custom recipe scripts, you can adapt Prodigy to read and write
                    data however you like, and plug in custom models using any of your favourite
                    frameworks.
                </p>

                <LogoRow
                    logos={[
                        { name: 'spacy', url: 'https://spacy.io', width: 95 },
                        { name: 'pytorch', url: 'https://pytorch.org', width: 110 },
                        { name: 'tensorflow', url: 'https://www.tensorflow.org', width: 140 },
                        {
                            name: 'jupyter',
                            url: 'https://github.com/explosion/jupyterlab-prodigy',
                            width: 130,
                        },
                    ]}
                />
            </Column>
            <Column>
                <CodeBlock lang="python" title="recipe.py" small>
                    {`import prodigy
from prodigy.components.loaders import JSONL

@prodigy.recipe("custom")
def custom_recipe(dataset, source):
    return {
        "dataset": dataset,
        "stream": JSONL(source),
        "view_id": "classification"
    }
`}
                </CodeBlock>
                <Terminal title="Command-line usage">
                    <Command>prodigy</Command>
                    <Command name="recipe">custom</Command>
                    <Command name="dataset">my_dataset</Command>
                    <Command name="source">./data.jsonl</Command>
                    <Command name="file">-F recipe.py</Command>
                </Terminal>
            </Column>
        </Section>

        <FeaturesRow />

        <Footer />
    </Layout>
)
